import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Avatar, Typography, TextField, Box, Divider,
    FormControlLabel, Radio, RadioGroup, Alert, IconButton, Tooltip,
    InputAdornment
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { IUser } from '../../../models/IUser';
import UserAdminServiceInstance from '../../../services/UserAdminService';
import { getApiErrorDescription } from '../../../services/ServiceConstants';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(2),
        },
        avatar: {
            width: 64,
            height: 64,
        },
        section: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
        },
        sectionTitle: {
            marginBottom: theme.spacing(1),
        },
    }));

interface IUserDetailDialogProps {
    user: IUser;
    isOrganizer: boolean;
    onClose: () => void;
    onUserUpdated: () => void;
}

export default function UserDetailDialog(props: IUserDetailDialogProps) {
    const classes = useStyles();
    const { t } = useTranslation("common");
    const { user, isOrganizer, onClose, onUserUpdated } = props;

    // Organizer role state
    const [currentIsOrganizer, setCurrentIsOrganizer] = useState(isOrganizer);
    const [roleLoading, setRoleLoading] = useState(false);

    // Password reset state
    const [passwordMode, setPasswordMode] = useState<'generate' | 'specify'>('generate');
    const [specifiedPassword, setSpecifiedPassword] = useState('');
    const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
    const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);

    // Edit user state
    const [webPage, setWebPage] = useState(user.webPage || '');
    const [comment, setComment] = useState(user.comment || '');
    const [picture, setPicture] = useState<string | undefined>(user.picture);
    const [portraitBlobAddressUri, setPortraitBlobAddressUri] = useState(user.portraitBlobAddressUri || '');
    const [editSuccess, setEditSuccess] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);

    // Update email state
    const [newEmail, setNewEmail] = useState(user.email || '');
    const [emailSuccess, setEmailSuccess] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);

    const handleToggleOrganizerRole = async () => {
        setRoleLoading(true);
        try {
            if (currentIsOrganizer) {
                await UserAdminServiceInstance.removeOrganizerRole(user.userId);
                setCurrentIsOrganizer(false);
            } else {
                await UserAdminServiceInstance.assignOrganizerRole(user.userId);
                setCurrentIsOrganizer(true);
            }
            onUserUpdated();
        } catch (e) {
            console.error(e);
        } finally {
            setRoleLoading(false);
        }
    };

    const handleResetPassword = async () => {
        setPasswordError(null);
        setPasswordResetSuccess(false);
        setGeneratedPassword(null);
        try {
            const newPwd = passwordMode === 'specify' ? specifiedPassword : undefined;
            const result = await UserAdminServiceInstance.resetPassword(user.userId, newPwd);
            setPasswordResetSuccess(true);
            if (result.generatedPassword) {
                setGeneratedPassword(result.generatedPassword);
            }
        } catch (e) {
            setPasswordError(getApiErrorDescription(e));
        }
    };

    const handleCopyPassword = () => {
        if (generatedPassword) {
            navigator.clipboard.writeText(generatedPassword);
        }
    };

    const handlePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.onloadend = () => {
                setPicture(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveUser = async () => {
        setEditError(null);
        setEditSuccess(false);
        try {
            const updatedUser = await UserAdminServiceInstance.updateUser(user.userId, {
                webPage,
                comment,
                picture,
            });
            setPortraitBlobAddressUri(updatedUser.portraitBlobAddressUri || portraitBlobAddressUri);
            setEditSuccess(true);
            onUserUpdated();
        } catch (e) {
            setEditError(getApiErrorDescription(e));
        }
    };

    const handleUpdateEmail = async () => {
        setEmailError(null);
        setEmailSuccess(false);
        try {
            await UserAdminServiceInstance.updateEmail(user.userId, newEmail);
            setEmailSuccess(true);
            onUserUpdated();
        } catch (e) {
            setEmailError(getApiErrorDescription(e));
        }
    };

    return (
        <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{t("adminUserManagement.userDetails")}</DialogTitle>
            <DialogContent>
                {/* User header */}
                <Box className={classes.header}>
                    <Avatar
                        alt={user.name}
                        src={picture || portraitBlobAddressUri}
                        className={classes.avatar}
                    />
                    <Box>
                        <Typography variant="h6">{user.name}</Typography>
                        <Typography variant="body2" color="textSecondary">{user.email}</Typography>
                    </Box>
                </Box>

                <Divider />

                {/* Organizer Role Section */}
                <Box className={classes.section}>
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                        {t("adminUserManagement.organizerRole")}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        {currentIsOrganizer
                            ? t("adminUserManagement.isOrganizer")
                            : t("adminUserManagement.isNotOrganizer")}
                    </Typography>
                    <Button
                        variant="outlined"
                        color={currentIsOrganizer ? "error" : "primary"}
                        onClick={handleToggleOrganizerRole}
                        disabled={roleLoading}
                    >
                        {currentIsOrganizer
                            ? t("adminUserManagement.demoteOrganizer")
                            : t("adminUserManagement.promoteOrganizer")}
                    </Button>
                </Box>

                <Divider />

                {/* Reset Password Section */}
                <Box className={classes.section}>
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                        {t("adminUserManagement.resetPassword")}
                    </Typography>
                    <RadioGroup
                        value={passwordMode}
                        onChange={(e) => {
                            setPasswordMode(e.target.value as 'generate' | 'specify');
                            setPasswordResetSuccess(false);
                            setGeneratedPassword(null);
                            setPasswordError(null);
                            setSpecifiedPassword('');
                        }}
                    >
                        <FormControlLabel
                            value="generate"
                            control={<Radio />}
                            label={t("adminUserManagement.generatePassword")}
                        />
                        <FormControlLabel
                            value="specify"
                            control={<Radio />}
                            label={t("adminUserManagement.specifyPassword")}
                        />
                    </RadioGroup>
                    {passwordMode === 'specify' && (
                        <TextField
                            fullWidth
                            label={t("adminUserManagement.newPassword")}
                            value={specifiedPassword}
                            onChange={(e) => setSpecifiedPassword(e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mt: 1, mb: 1 }}
                        />
                    )}
                    <Button
                        variant="outlined"
                        onClick={handleResetPassword}
                        disabled={passwordMode === 'specify' && !specifiedPassword}
                        sx={{ mt: 1 }}
                    >
                        {t("adminUserManagement.resetPasswordAction")}
                    </Button>
                    {passwordResetSuccess && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            {t("adminUserManagement.passwordResetSuccess")}
                        </Alert>
                    )}
                    {generatedPassword && (
                        <TextField
                            fullWidth
                            label={t("adminUserManagement.generatedPassword")}
                            value={generatedPassword}
                            variant="outlined"
                            size="small"
                            sx={{ mt: 1 }}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title={t("adminUserManagement.copyPassword")}>
                                            <IconButton onClick={handleCopyPassword} edge="end" size="small">
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                    {passwordError && (
                        <Alert severity="error" sx={{ mt: 1 }}>{passwordError}</Alert>
                    )}
                </Box>

                <Divider />

                {/* Update Email Section */}
                <Box className={classes.section}>
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                        {t("adminUserManagement.updateEmail")}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <TextField
                            fullWidth
                            label={t("adminUserManagement.email")}
                            value={newEmail}
                            onChange={(e) => { setNewEmail(e.target.value); setEmailSuccess(false); setEmailError(null); }}
                            variant="outlined"
                            size="small"
                            type="email"
                        />
                        <Button
                            variant="outlined"
                            onClick={handleUpdateEmail}
                            disabled={!newEmail || newEmail === user.email}
                        >
                            {t("adminUserManagement.updateEmailAction")}
                        </Button>
                    </Box>
                    {emailSuccess && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            {t("adminUserManagement.emailUpdated")}
                        </Alert>
                    )}
                    {emailError && (
                        <Alert severity="error" sx={{ mt: 1 }}>{emailError}</Alert>
                    )}
                </Box>

                <Divider />

                {/* Edit User Fields Section */}
                <Box className={classes.section}>
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                        {t("adminUserManagement.editUser")}
                    </Typography>
                    <TextField
                        fullWidth
                        label={t("adminUserManagement.webPage")}
                        value={webPage}
                        onChange={(e) => setWebPage(e.target.value)}
                        variant="outlined"
                        size="small"
                        type="url"
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label={t("adminUserManagement.comments")}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        variant="outlined"
                        size="small"
                        multiline
                        rows={3}
                        sx={{ mb: 2 }}
                    />
                    <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                        <Button variant="contained" component="label">
                            {t("adminUserManagement.uploadPicture")}
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={handlePictureChange}
                            />
                        </Button>
                        {(picture || portraitBlobAddressUri) && (
                            <Avatar
                                src={picture || portraitBlobAddressUri}
                                alt="Profile"
                                sx={{ width: 56, height: 56 }}
                            />
                        )}
                    </Box>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleSaveUser}
                    >
                        {t("adminUserManagement.saveChanges")}
                    </Button>
                    {editSuccess && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            {t("adminUserManagement.userUpdated")}
                        </Alert>
                    )}
                    {editError && (
                        <Alert severity="error" sx={{ mt: 1 }}>{editError}</Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    {t("commonActions.cancel")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
